import React, { useEffect, useState } from 'react';
import { X, Calendar, MapPin, AlertTriangle, Shield, Car, Hash, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge, SeverityBadge } from './shared/Badge';
import { LoadingSpinner, ErrorState } from './shared';
import { vinApi } from '../services/api';
import { FraudAnalysisBreakdown } from './FraudAnalysisBreakdown';
import { CarrierSwitchTimeline } from './CarrierSwitchTimeline';
import { CarrierRelationshipGraph } from './CarrierRelationshipGraph';
import { exportFraudReport } from '../utils/fraud-report-export';

interface VINDetailModalProps {
  vin: string;
  onClose: () => void;
}

interface VINDetails {
  vin: string;
  nhtsaData?: {
    make: string | null;
    model: string | null;
    modelYear: number | null;
    manufacturerName: string | null;
    vehicleType: string | null;
    bodyClass: string | null;
    gvwr: string | null;
    fuelType: string | null;
    engineCylinders: number | null;
    transmissionStyle: string | null;
    driveType: string | null;
  } | null;
  crashes?: Array<{
    reportDate: string;
    reportNumber: string;
    reportState: string;
    fatalities: number;
    injuries: number;
    towAway: string;
    location?: string;
    city?: string;
    hazmatReleased: string;
  }>;
  inspections?: Array<{
    inspectionId: string;
    inspectionDate: string;
    reportState: string;
    reportNumber: string;
    dotNumber: number;
    carrierName: string;
    inspectionLevel: string;
    location?: string;
    totalViolations: number;
    oosTotal: number;
    driverViolations: number;
    driverOos: number;
    vehicleViolations: number;
    vehicleOos: number;
    postAccident: boolean;
  }>;
  carriers?: Array<{
    dotNumber: number;
    legalName?: string;
    carrierName?: string;
    dbaName?: string;
    city?: string;
    state?: string;
    safetyRating?: string;
    firstSeen: string;
    lastSeen: string;
    crashCount: number;
    inspectionCount: number;
    violationCount: number;
    oosCount: number;
    cleanInspections: number;
    fatalCrashes: number;
    injuryCrashes: number;
    towAwayCrashes: number;
    daysSinceLastSeen: number;
  }>;
  shellGameDetected?: boolean;
  riskScore?: number;
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskFlags?: string[];
  timeline?: Array<{
    date: string;
    type: 'crash' | 'inspection';
    dotNumber: number;
    carrierName: string;
    severity: string;
    details: Record<string, unknown>;
  }>;
  totalEvents?: number;
  dataSource?: {
    crashRecords: number;
    inspectionRecords: number;
    totalDatabaseVINs: number;
  };
  fraudScore?: {
    score: number;
    level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    indicators: string[];
  };
  isRentalUnit?: boolean;
  rentalCompanies?: Array<{
    dotNumber: string;
    companyName: string;
  }>;
}

// Timeline data for advanced fraud analysis
interface TimelineData {
  vin: string;
  totalEvents: number;
  uniqueCarriers: number;
  switchCount: number;
  suspiciousSwitches: number;
  switches: Array<{
    fromDotNumber: number;
    fromCarrierName: string;
    lastSeenAt: string;
    toDotNumber: number;
    toCarrierName: string;
    firstSeenAt: string;
    daysBetween: number;
    suspicious: boolean;
    suspicionReasons: string[];
  }>;
  carrierPeriods: Array<{
    dotNumber: number;
    carrierName: string;
    startDate: string;
    endDate: string;
    eventCount: number;
  }>;
  timeline: Array<any>;
  advancedFraudAnalysis?: {
    level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    reason: string;
    details: string[];
    fraudDetected: boolean;
    phase1RedFlags: {
      badEventQuickSwitchClean: boolean;
      rapidBackAndForth: boolean;
      ghostCarrierViolation: boolean;
      dumpAndReturnAfterIdle: boolean;
      shellCarrierOnlyBadEvents: boolean;
    };
    phase2Indicators: {
      relatedCarriers: boolean;
      sameOwner: boolean;
      sameAddress: boolean;
      samePhone: boolean;
      recentlyCreatedCarriers: boolean;
    };
    phase3Whitelist: {
      longGaps: boolean;
      legitimateSale: boolean;
      linearProgression: boolean;
      rentalUnit: boolean;
      postRevocationDispersal: boolean;
    };
    decisionMatrix: {
      condition: string;
      action: string;
    };
    carrierStatuses?: Array<{
      dotNumber: number;
      carrierName: string;
      status: string;
      statusCode: string;
    }>;
  };
  isRentalUnit?: boolean;
  rentalCompanies?: Array<{ dotNumber: string; companyName: string }>;
}

export const VINDetailModal: React.FC<VINDetailModalProps> = ({ vin, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [details, setDetails] = useState<VINDetails | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [showAdvancedFraud, setShowAdvancedFraud] = useState(true); // Changed to true - always show fraud analysis by default

  const fetchVINDetails = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch both lookup data and timeline data in parallel
      const [lookupData, timeline] = await Promise.all([
        vinApi.getVINLookup(vin),
        vinApi.getVINTimeline(vin).catch(() => null) // Gracefully handle if timeline fails
      ]);

      setDetails(lookupData);
      if (timeline) {
        setTimelineData(timeline);
        // Always show fraud analysis by default (user can collapse if desired)
        setShowAdvancedFraud(true);
      }
    } catch (err: unknown) {
      console.error('Error fetching VIN details:', err);
      const errorMessage = err instanceof Error
        ? (err as any).response?.data?.message || err.message
        : 'Failed to load VIN details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVINDetails();
  }, [vin]);

  // Export handler for fraud report
  const handleExportFraudReport = () => {
    if (timelineData) {
      exportFraudReport(vin, timelineData);
    }
  };

  // Handle click outside modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-xl font-bold">VIN Intelligence Report</h2>
              <p className="text-sm text-blue-100 font-mono mt-1">{vin}</p>
            </div>
            {!loading && details?.isRentalUnit && (
              <span style={{
                background: 'white',
                color: '#1e40af',
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                🚙 RENTAL UNIT
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner message="Loading VIN details..." size="lg" />
              <p className="mt-4 text-gray-600">Loading VIN intelligence...</p>
            </div>
          )}

          {error && (
            <ErrorState
              title="VIN Details Unavailable"
              message={error}
              onRetry={fetchVINDetails}
              variant="default"
              icon="error"
            />
          )}

          {!loading && !error && details && (
            <div className="space-y-6">
              {/* Carrier Switch Timeline Visualization - MOVED TO TOP FOR PROMINENCE */}
              {timelineData && timelineData.switches && timelineData.switches.length > 0 && (
                <div className="border-4 border-orange-300 bg-orange-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                    <h3 className="text-xl font-bold text-gray-900">🚨 VIN Fraud Timeline Analysis</h3>
                    <Badge variant="warning" size="lg">{timelineData.uniqueCarriers} carriers</Badge>
                  </div>
                  <CarrierSwitchTimeline
                    switches={timelineData.switches}
                    carrierPeriods={timelineData.carrierPeriods}
                    timeline={timelineData.timeline}
                  />
                </div>
              )}

              {/* NHTSA Vehicle Decode Data */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Car className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Vehicle Information</h3>
                  <Badge variant="info" size="sm">NHTSA Decode</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Make:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.make || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Model:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.model || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Year:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.modelYear || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Manufacturer:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.manufacturerName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Vehicle Type:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.vehicleType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Body Class:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.bodyClass || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">GVWR:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.gvwr || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Fuel Type:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.fuelType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Engine:</span>
                    <p className="font-semibold text-gray-900">
                      {details.nhtsaData?.engineCylinders ? `${details.nhtsaData.engineCylinders} cylinders` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Transmission:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.transmissionStyle || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Drive Type:</span>
                    <p className="font-semibold text-gray-900">{details.nhtsaData?.driveType || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Rental Unit Notice */}
              {details.isRentalUnit && details.rentalCompanies && (
                <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Car className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-gray-900">Rental Unit Status</h3>
                  </div>
                  <div className="space-y-3">
                    <p className="text-gray-700">
                      This VIN has been identified as a <strong>rental unit</strong>. Transfers between carriers are expected and do not necessarily indicate fraudulent activity.
                    </p>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Associated with rental companies:</p>
                      {details.rentalCompanies.map((company) => (
                        <div key={company.dotNumber} className="inline-flex items-center gap-2 mr-3 mb-2">
                          <Badge variant="info" size="sm">
                            DOT {company.dotNumber}
                          </Badge>
                          <span className="text-sm text-gray-700">{company.companyName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Risk Score - Now from 8.6M VINs */}
              {details.riskScore !== undefined && (
                <div className={`border border-gray-200 rounded-lg p-6 ${
                  details.isRentalUnit ? 'bg-gradient-to-br from-blue-50 to-gray-50' : 'bg-gradient-to-br from-red-50 to-orange-50'
                }`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-red-600" />
                    <h3 className="text-lg font-bold text-gray-900">Enhanced Shell Game Risk Assessment</h3>
                    <Badge variant="info" size="sm">Across 8.6M VINs</Badge>
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-5xl font-bold text-red-600">{details.riskScore}</div>
                    <div>
                      <Badge
                        variant={
                          details.riskLevel === 'CRITICAL' ? 'danger' :
                          details.riskLevel === 'HIGH' ? 'danger' :
                          details.riskLevel === 'MEDIUM' ? 'warning' : 'success'
                        }
                        size="lg"
                      >
                        {details.riskLevel} RISK
                      </Badge>
                      <p className="text-sm text-gray-600 mt-1">Risk score (0-100)</p>
                      {details.dataSource && (
                        <p className="text-xs text-gray-500 mt-1">
                          Based on {details.dataSource.crashRecords} crash records + {details.dataSource.inspectionRecords} inspection records
                        </p>
                      )}
                    </div>
                  </div>
                  {details.riskFlags && Array.isArray(details.riskFlags) && details.riskFlags.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700">Risk Indicators:</p>
                      <ul className="space-y-2">
                        {details.riskFlags.map((flag, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2 bg-white rounded p-2 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span>{flag}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Toggle for Advanced Fraud Analysis */}
                  {timelineData?.advancedFraudAnalysis && (
                    <button
                      onClick={() => setShowAdvancedFraud(!showAdvancedFraud)}
                      className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      {showAdvancedFraud ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Advanced Phase 1/2/3 Analysis
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View Advanced Phase 1/2/3 Analysis
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Advanced Fraud Analysis Breakdown - Phase 1/2/3 */}
              {showAdvancedFraud && timelineData?.advancedFraudAnalysis && (
                <div className="mt-6">
                  <FraudAnalysisBreakdown
                    fraudAnalysis={timelineData.advancedFraudAnalysis}
                    vin={vin}
                    onExport={handleExportFraudReport}
                  />
                </div>
              )}
              {/* Carrier Relationship Graph */}
              {timelineData?.advancedFraudAnalysis && timelineData.carrierPeriods && timelineData.carrierPeriods.length > 1 && (
                <div className="mt-6">
                  <CarrierRelationshipGraph
                    carriers={timelineData.carrierPeriods.map(p => ({
                      dotNumber: p.dotNumber,
                      carrierName: p.carrierName,
                      status: timelineData.advancedFraudAnalysis?.carrierStatuses?.find(cs => cs.dotNumber === p.dotNumber)?.status,
                      statusCode: timelineData.advancedFraudAnalysis?.carrierStatuses?.find(cs => cs.dotNumber === p.dotNumber)?.statusCode,
                    }))}
                    phase2Indicators={timelineData.advancedFraudAnalysis.phase2Indicators}
                  />
                </div>
              )}

              {/* Crash History */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-bold text-gray-900">Crash History</h3>
                  <Badge variant="danger" size="sm">{details.crashes?.length || 0} crashes</Badge>
                </div>
                {details.crashes && details.crashes.length > 0 ? (
                  <div className="space-y-3">
                    {details.crashes.map((crash, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          crash.fatalities > 0 ? 'bg-red-50 border-red-200' :
                          crash.injuries > 0 ? 'bg-yellow-50 border-yellow-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">
                              {new Date(crash.reportDate).toLocaleDateString()}
                            </span>
                            <Badge variant="info" size="sm">{crash.reportState}</Badge>
                          </div>
                          <SeverityBadge
                            severity={
                              crash.fatalities > 0 ? 'critical' :
                              crash.injuries > 0 ? 'high' : 'medium'
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Fatalities:</span>
                            <p className={`font-semibold ${crash.fatalities > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {crash.fatalities}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Injuries:</span>
                            <p className={`font-semibold ${crash.injuries > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                              {crash.injuries}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Tow Away:</span>
                            <p className="font-semibold text-gray-900">{crash.towAway === 'Y' ? 'Yes' : 'No'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Hazmat:</span>
                            <p className="font-semibold text-gray-900">{crash.hazmatReleased === 'Y' ? 'Yes' : 'No'}</p>
                          </div>
                        </div>
                        {(crash.location || crash.city) && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{crash.location || crash.city}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No crash history found for this VIN</p>
                )}
              </div>

              {/* Inspection History */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-gray-900">Inspection History</h3>
                  <Badge variant="info" size="sm">{details.inspections?.length || 0} inspections</Badge>
                </div>
                {details.inspections && details.inspections.length > 0 ? (
                  <div className="space-y-3">
                    {details.inspections.map((inspection, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          inspection.oosTotal > 0 ? 'bg-red-50 border-red-200' :
                          inspection.totalViolations > 0 ? 'bg-yellow-50 border-yellow-200' :
                          'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">
                              {new Date(inspection.inspectionDate).toLocaleDateString()}
                            </span>
                            <Badge variant="info" size="sm">{inspection.reportState}</Badge>
                            <Badge variant="info" size="sm">Level {inspection.inspectionLevel}</Badge>
                          </div>
                          <SeverityBadge
                            severity={
                              inspection.oosTotal > 0 ? 'critical' :
                              inspection.totalViolations > 0 ? 'high' : 'low'
                            }
                          />
                        </div>
                        <div className="text-sm text-gray-700 mb-2">
                          <strong>Carrier:</strong> {inspection.carrierName} (DOT {inspection.dotNumber})
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Total Violations:</span>
                            <p className={`font-semibold ${inspection.totalViolations > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                              {inspection.totalViolations}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Out-of-Service:</span>
                            <p className={`font-semibold ${inspection.oosTotal > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {inspection.oosTotal}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Driver Violations:</span>
                            <p className="font-semibold text-gray-900">{inspection.driverViolations}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Vehicle Violations:</span>
                            <p className="font-semibold text-gray-900">{inspection.vehicleViolations}</p>
                          </div>
                        </div>
                        {inspection.postAccident && (
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            <span className="text-red-600 font-semibold">Post-Accident Inspection</span>
                          </div>
                        )}
                        {inspection.location && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{inspection.location}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No inspection history found for this VIN</p>
                )}
              </div>

              {/* Enhanced Carrier History - With Inspections & Violations */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Hash className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">Carrier History</h3>
                  <Badge variant="info" size="sm">Shell Game Detection</Badge>
                  {details.shellGameDetected && (
                    <Badge variant="danger" size="sm">
                      {details.carriers?.length || 0} Carriers
                    </Badge>
                  )}
                </div>
                {details.carriers && details.carriers.length > 0 ? (
                  <div className="space-y-4">
                    {details.carriers.map((carrier, idx) => (
                      <div
                        key={idx}
                        className={`p-5 rounded-lg border-2 ${
                          carrier.oosCount > 0 || carrier.fatalCrashes > 0
                            ? 'bg-red-50 border-red-300'
                            : carrier.violationCount > 0
                            ? 'bg-yellow-50 border-yellow-300'
                            : 'bg-green-50 border-green-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-bold text-gray-900 text-lg">
                              {carrier.legalName || carrier.carrierName || 'Unknown Carrier'}
                            </p>
                            <p className="text-sm text-gray-600">
                              DOT {carrier.dotNumber}
                              {carrier.dbaName && <span> (DBA: {carrier.dbaName})</span>}
                            </p>
                            {carrier.city && carrier.state && (
                              <p className="text-xs text-gray-500 mt-1">
                                {carrier.city}, {carrier.state}
                                {carrier.safetyRating && <span> • Safety Rating: {carrier.safetyRating}</span>}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            {carrier.crashCount > 0 && (
                              <Badge variant="danger" size="sm">{carrier.crashCount} crashes</Badge>
                            )}
                            {carrier.inspectionCount > 0 && (
                              <Badge variant="info" size="sm">{carrier.inspectionCount} inspections</Badge>
                            )}
                          </div>
                        </div>

                        {/* Comprehensive Metrics Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3 bg-white rounded p-3 border border-gray-200">
                          <div>
                            <span className="text-gray-600 text-xs block">Crashes:</span>
                            <p className="font-bold text-gray-900">{carrier.crashCount}</p>
                            {carrier.fatalCrashes > 0 && (
                              <p className="text-xs text-red-600">{carrier.fatalCrashes} fatal</p>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-600 text-xs block">Inspections:</span>
                            <p className="font-bold text-gray-900">{carrier.inspectionCount}</p>
                            {carrier.cleanInspections > 0 && (
                              <p className="text-xs text-green-600">{carrier.cleanInspections} clean</p>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-600 text-xs block">Violations:</span>
                            <p className={`font-bold ${carrier.violationCount > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                              {carrier.violationCount}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600 text-xs block">Out-of-Service:</span>
                            <p className={`font-bold ${carrier.oosCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {carrier.oosCount}
                            </p>
                          </div>
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                          <div>
                            <span className="text-gray-600">First Seen:</span>
                            <p className="font-semibold text-gray-900">
                              {new Date(carrier.firstSeen).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Last Seen:</span>
                            <p className="font-semibold text-gray-900">
                              {new Date(carrier.lastSeen).toLocaleDateString()}
                              <span className="text-xs text-gray-500 ml-2">
                                ({carrier.daysSinceLastSeen} days ago)
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    This vehicle has only been associated with one carrier
                  </p>
                )}
              </div>

              {/* Enhanced Timeline - Crashes + Inspections */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-gray-900">Complete Event Timeline</h3>
                  {details.timeline && (
                    <Badge variant="info" size="sm">{details.totalEvents} total events</Badge>
                  )}
                </div>
                <div className="relative border-l-2 border-gray-200 ml-4 space-y-4">
                  {details.timeline && details.timeline.length > 0 ? (
                    details.timeline.map((event, idx) => (
                      <div key={idx} className="relative pl-6 pb-4">
                        <div
                          className={`absolute -left-2 top-0 w-4 h-4 rounded-full border-2 border-white ${
                            event.type === 'crash'
                              ? event.severity === 'fatal'
                                ? 'bg-red-700'
                                : event.severity === 'injury'
                                ? 'bg-red-500'
                                : 'bg-orange-400'
                              : event.severity === 'oos'
                              ? 'bg-red-500'
                              : event.severity === 'violation'
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                        />
                        <div className="text-xs text-gray-500 mb-1">
                          {new Date(event.date).toLocaleDateString()}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {event.type === 'crash' ? (
                            <span>
                              Crash - DOT {event.dotNumber}
                              {(event.details.fatalities as number) > 0 && (
                                <span className="text-red-600 ml-2">({event.details.fatalities as React.ReactNode} fatal)</span>
                              )}
                              {(event.details.injuries as number) > 0 && event.details.fatalities === 0 && (
                                <span className="text-yellow-600 ml-2">({event.details.injuries as React.ReactNode} injured)</span>
                              )}
                            </span>
                          ) : (
                            <span>
                              Inspection - DOT {event.dotNumber}
                              {(event.details.oos as number) > 0 && (
                                <span className="text-red-600 ml-2">({event.details.oos as React.ReactNode} OOS)</span>
                              )}
                              {(event.details.violations as number) > 0 && event.details.oos === 0 && (
                                <span className="text-yellow-600 ml-2">({event.details.violations as React.ReactNode} violations)</span>
                              )}
                              {event.details.violations === 0 && event.details.oos === 0 && (
                                <span className="text-green-600 ml-2">(Clean)</span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {event.carrierName || 'Unknown Carrier'}
                          {event.details.location && <span> • {event.details.location as React.ReactNode}</span>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 pl-6">No events recorded</p>
                  )}
                </div>
              </div>

              {/* Shell Game Warning */}
              {details.carriers && details.carriers.length > 1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-yellow-900">Potential Shell Game Detected</p>
                      <p className="text-sm text-yellow-800 mt-1">
                        This vehicle has been associated with multiple carriers. This could indicate a "shell game"
                        where vehicles are moved between companies to hide violations and crash history.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
